import {FormControl, FormField} from 'platform/components';
import {Box, Heading, HStack, Show, Space, VStack} from 'platform/foundation';

import {useEffect} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';
import {useLocation} from 'react-router-dom';

import {indexBy, isNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {SeriesType, useGetDefinitionTemplateListQuery, useGetSeriesListQuery} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {useGetServiceOrderVariantAvailableDocumentsQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {getOptionsFromSeries, SettingsFooter, SettingsSection} from '@dms/settings/shared';
import {useGetCurrentBranch, useTenant} from '@dms/shared';

import {useNavigate} from 'shared/web';

import {FormType} from '../../types/ServiceOrderVariant';
import {getServiceOrderVariantsRoutes} from '../../utils/getServiceOrderVariantsRoutes';

interface DocumentsTabProps {
  control: FormControl<FormType>;
  formApi: UseFormReturn<FormType>;
  serviceOrderTypeCode?: string | null;
  serviceOrderVariantId?: string;
}

type TemplateOption = {label: string; value: string};
type GroupedOptions = {label?: string; options: TemplateOption[]};

type TemplateData = {
  options: GroupedOptions[];
  primaryTemplateId: string | undefined;
};

const ORDER_SHEET_CODES = [
  'workshop-order_list_commercial',
  'workshop-order_list_contract',
  'workshop-order_list_internal',
  'workshop-order_list_insurance',
  'workshop-order_list_warranty',
] as const;

export function DocumentsTab(props: DocumentsTabProps) {
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const serviceOrderVariantsRoutes = getServiceOrderVariantsRoutes(pathname);

  const isFastTrackEnabled = useWatch({
    control: props.control,
    name: 'documents.fastTrackIssue.isEnable',
  });

  const serviceOrderTypeId = props.formApi.watch('general.serviceOrderTypeId');
  const serviceCaseTypeId = props.formApi.watch('general.serviceCaseTypeId');

  // Get branch and tenant for authorization profiles query
  const {activeBranchId} = useGetCurrentBranch();
  const {tenant} = useTenant();

  const {data: availableDocuments, isLoading: isLoadingAvailableDocuments} =
    useGetServiceOrderVariantAvailableDocumentsQuery(
      props.serviceOrderVariantId
        ? {serviceOrderVariantId: props.serviceOrderVariantId}
        : {
            serviceOrderTypeId: serviceOrderTypeId ?? undefined,
            serviceCaseTypeId: serviceCaseTypeId ?? undefined,
          },
      {skip: !props.serviceOrderVariantId && isNilOrEmpty(serviceOrderTypeId)}
    );

  // Fetch definition template list to get primary template info
  const {data: definitionTemplateList} = useGetDefinitionTemplateListQuery({});

  // Create lookup map for definitions by documentCode
  const getDefinitionsByCode = () => {
    if (!definitionTemplateList?.definitions) {
      return {};
    }
    return indexBy((def) => def.documentCode, definitionTemplateList.definitions);
  };

  // Fetch authorization profiles to get names for group labels
  const {data: authorizationProfiles} = useGetAuthorizationProfilesQuery(
    {branchId: activeBranchId, 'x-tenant': tenant?.id ?? ''},
    {skip: isNil(tenant)}
  );

  // Create lookup map for authorization profiles by ID
  const getAuthorizationProfilesById = () =>
    authorizationProfiles ? indexBy((item) => item.id, authorizationProfiles) : {};

  const {data: series, isLoading: isSeriesLoading} = useGetSeriesListQuery({
    type: [
      'workshop/service_order_list',
      'workshop/service_order_offer',
      'workshop/service_order_issue',
      'workshop/service_order_parts_return',
      'workshop/service_order_claim',
      'warehouse/fast-track',
    ],
  });

  /**
   * Check if a template is primary based on definition data.
   * A template is primary if:
   * - It's the definition's primaryTemplateId (for default templates)
   * - It's the authorization profile's primaryTemplateId (for auth profile templates)
   */
  const isTemplatePrimary = (
    templateId: string,
    documentCode: string,
    authorizationProfileId: string | null
  ): boolean => {
    const definitionsByCode = getDefinitionsByCode();
    const definition = definitionsByCode[documentCode];
    if (!definition) {
      return false;
    }

    // For templates with authorization profile, check auth profile's primary
    if (isNotNilOrEmpty(authorizationProfileId)) {
      const authProfilePrimary = definition.authorizationProfiles.find(
        (ap) => ap.authorizationProfileId === authorizationProfileId
      )?.primaryTemplateId;
      return templateId === authProfilePrimary;
    }

    // For default templates (no auth profile), check definition's primary
    return templateId === definition.primaryTemplateId;
  };

  /**
   * Get templates with grouping by authorization profile.
   * Returns grouped options for FormField and the primary template ID for prefilling.
   * Priority: templates with auth profile > default templates
   */
  const getTemplatesWithGrouping = (documentCodes: string | string[]): TemplateData => {
    if (!availableDocuments) {
      return {options: [], primaryTemplateId: undefined};
    }

    const authorizationProfilesById = getAuthorizationProfilesById();
    const codes = Array.isArray(documentCodes) ? documentCodes : [documentCodes];
    const allTemplates: Array<{
      id: string;
      title: string;
      documentCode: string;
      authorizationProfileId: string | null;
      isPrimary: boolean;
    }> = [];
    const seenIds = new Set<string>();

    for (const code of codes) {
      const doc = availableDocuments.find((d) => d.documentCode === code);
      if (doc) {
        for (const template of doc.templates) {
          if (!seenIds.has(template.id)) {
            seenIds.add(template.id);
            allTemplates.push({
              id: template.id,
              title: template.title,
              documentCode: code,
              authorizationProfileId: template.authorizationProfileId,
              isPrimary: isTemplatePrimary(template.id, code, template.authorizationProfileId),
            });
          }
        }
      }
    }

    // Separate templates by authorization profile
    const templatesWithAuth = allTemplates.filter((t) => isNotNilOrEmpty(t.authorizationProfileId));
    const defaultTemplates = allTemplates.filter((t) => isNilOrEmpty(t.authorizationProfileId));

    // Find primary template: first check templates with auth, then default
    // Sort by isPrimary so primary templates come first
    const sortedAuthTemplates = [...templatesWithAuth].sort(
      (a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0)
    );
    const sortedDefaultTemplates = [...defaultTemplates].sort(
      (a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0)
    );

    const primaryTemplate =
      sortedAuthTemplates.find((t) => t.isPrimary) ??
      sortedDefaultTemplates.find((t) => t.isPrimary) ??
      sortedAuthTemplates[0] ??
      sortedDefaultTemplates[0];

    // Build grouped options
    const options: GroupedOptions[] = [];

    // Group 1: Templates with authorization profile - use actual profile name
    if (isNotNilOrEmpty(sortedAuthTemplates)) {
      // Get unique authorization profile IDs from templates
      const authProfileIds = [
        ...new Set(sortedAuthTemplates.map((t) => t.authorizationProfileId).filter(Boolean)),
      ];

      // Group templates by authorization profile ID
      for (const profileId of authProfileIds) {
        const profileTemplates = sortedAuthTemplates.filter(
          (t) => t.authorizationProfileId === profileId
        );
        const profileName = authorizationProfilesById[profileId ?? '']?.name;

        options.push({
          label: profileName ?? i18n.t('general.labels.template'),
          options: profileTemplates.map((t) => ({label: t.title, value: t.id})),
        });
      }
    }

    // Group 2: Default templates (without authorization)
    if (isNotNilOrEmpty(sortedDefaultTemplates)) {
      options.push({
        label: i18n.t('entity.authorizationProfiles.labels.withoutAuthorization'),
        options: sortedDefaultTemplates.map((t) => ({label: t.title, value: t.id})),
      });
    }

    return {
      options,
      primaryTemplateId: primaryTemplate?.id,
    };
  };

  const getSeriesOptions = (type: SeriesType) =>
    series?.filter((s) => s.type === type).map((s) => ({label: s.name, value: s.id})) ?? [];

  const hasDocument = (documentCode: string) => {
    if (!availableDocuments) {
      return true;
    }
    return availableDocuments.some((doc) => doc.documentCode === documentCode);
  };

  const shouldShowOrderSheet =
    ORDER_SHEET_CODES.some((code) => hasDocument(code)) ||
    (!availableDocuments && !isLoadingAvailableDocuments);

  const shouldShowOrderOffer = hasDocument('workshop-service_order_offer');
  const shouldShowHandlingProtocol = hasDocument('workshop-handling_protocol');
  const shouldShowResolutionProtocol = hasDocument('workshop-resolution_protocol');

  const orderSheetData = getTemplatesWithGrouping([...ORDER_SHEET_CODES]);
  const orderOfferData = getTemplatesWithGrouping('workshop-service_order_offer');
  const handlingProtocolData = getTemplatesWithGrouping('workshop-handling_protocol');
  const resolutionProtocolData = getTemplatesWithGrouping('workshop-resolution_protocol');
  const serviceOrderIssueData = getTemplatesWithGrouping('warehouse-service-order-issue-html');
  const serviceOrderReturnData = getTemplatesWithGrouping('warehouse-service-order-return-html');
  const fastTrackData = getTemplatesWithGrouping('workshop-fast_track');

  // Prefill form with primary templates when availableDocuments loads
  useEffect(() => {
    if (isLoadingAvailableDocuments || !availableDocuments) {
      return;
    }

    // Order Sheet
    const orderSheetTemplateId = props.formApi.getValues('documents.orderSheet.templateId');
    if (shouldShowOrderSheet && isNilOrEmpty(orderSheetTemplateId)) {
      if (orderSheetData.primaryTemplateId) {
        props.formApi.setValue(
          'documents.orderSheet.templateId',
          orderSheetData.primaryTemplateId,
          {
            shouldValidate: true,
          }
        );
      }
    }

    // Order Offer
    const orderOfferTemplateId = props.formApi.getValues('documents.orderOffer.templateId');
    if (shouldShowOrderOffer && isNilOrEmpty(orderOfferTemplateId)) {
      if (orderOfferData.primaryTemplateId) {
        props.formApi.setValue(
          'documents.orderOffer.templateId',
          orderOfferData.primaryTemplateId,
          {
            shouldValidate: true,
          }
        );
      }
    }

    // Handling Protocol
    const handlingProtocolTemplateId = props.formApi.getValues(
      'documents.orderHandlingProtocol.templateId'
    );
    if (shouldShowHandlingProtocol && isNilOrEmpty(handlingProtocolTemplateId)) {
      if (handlingProtocolData.primaryTemplateId) {
        props.formApi.setValue(
          'documents.orderHandlingProtocol.templateId',
          handlingProtocolData.primaryTemplateId,
          {
            shouldValidate: true,
          }
        );
      }
    }

    // Resolution Protocol
    const resolutionProtocolTemplateId = props.formApi.getValues(
      'documents.orderResolutionProtocol.templateId'
    );
    if (shouldShowResolutionProtocol && isNilOrEmpty(resolutionProtocolTemplateId)) {
      if (resolutionProtocolData.primaryTemplateId) {
        props.formApi.setValue(
          'documents.orderResolutionProtocol.templateId',
          resolutionProtocolData.primaryTemplateId,
          {
            shouldValidate: true,
          }
        );
      }
    }

    // Service Order Issue
    const serviceOrderIssueTemplateId = props.formApi.getValues(
      'documents.serviceOrderIssue.templateId'
    );
    if (isNilOrEmpty(serviceOrderIssueTemplateId)) {
      if (serviceOrderIssueData.primaryTemplateId) {
        props.formApi.setValue(
          'documents.serviceOrderIssue.templateId',
          serviceOrderIssueData.primaryTemplateId,
          {
            shouldValidate: true,
          }
        );
      }
    }

    // Service Order Return
    const serviceOrderReturnTemplateId = props.formApi.getValues(
      'documents.serviceOrderReturn.templateId'
    );
    if (isNilOrEmpty(serviceOrderReturnTemplateId)) {
      if (serviceOrderReturnData.primaryTemplateId) {
        props.formApi.setValue(
          'documents.serviceOrderReturn.templateId',
          serviceOrderReturnData.primaryTemplateId,
          {
            shouldValidate: true,
          }
        );
      }
    }

    // Fast Track Issue
    const fastTrackTemplateId = props.formApi.getValues('documents.fastTrackIssue.templateId');
    if (isNilOrEmpty(fastTrackTemplateId)) {
      if (fastTrackData.primaryTemplateId) {
        props.formApi.setValue(
          'documents.fastTrackIssue.templateId',
          fastTrackData.primaryTemplateId,
          {
            shouldValidate: true,
          }
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    availableDocuments,
    isLoadingAvailableDocuments,
    orderSheetData.primaryTemplateId,
    orderOfferData.primaryTemplateId,
    handlingProtocolData.primaryTemplateId,
    resolutionProtocolData.primaryTemplateId,
    serviceOrderIssueData.primaryTemplateId,
    serviceOrderReturnData.primaryTemplateId,
    fastTrackData.primaryTemplateId,
  ]);

  return (
    <SettingsSection>
      <VStack spacing={4}>
        <Heading size={4}>{i18n.t('general.labels.documents')}</Heading>
        {shouldShowOrderSheet && (
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="documents.orderSheet.templateId"
                type="choice"
                label={getTemplateLabel('entity.order.actions.orderList')}
                options={orderSheetData.options}
                isLoading={isLoadingAvailableDocuments}
                data-testid={testIds.settings.serviceOrderVariantsDetail('orderListTemplate')}
              />
            </Box>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="documents.orderSheet.docSeriesId"
                type="choice"
                label={getSeriesLabel('entity.order.actions.orderList')}
                options={getSeriesOptions('workshop/service_order_list')}
                isLoading={isSeriesLoading}
                isDisabled={isNilOrEmpty(props.formApi.watch('documents.orderSheet.templateId'))}
                data-testid={testIds.settings.serviceOrderVariantsDetail('orderListSeries')}
              />
            </Box>
          </HStack>
        )}
        {shouldShowOrderOffer && (
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="documents.orderOffer.templateId"
                type="choice"
                label={getTemplateLabel('entity.interest.labels.offer')}
                options={orderOfferData.options}
                isLoading={isLoadingAvailableDocuments}
                data-testid={testIds.settings.serviceOrderVariantsDetail('offerTemplate')}
              />
            </Box>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="documents.orderOffer.docSeriesId"
                type="choice"
                label={getSeriesLabel('entity.interest.labels.offer')}
                options={getSeriesOptions('workshop/service_order_offer')}
                isLoading={isSeriesLoading}
                isDisabled={isNilOrEmpty(props.formApi.watch('documents.orderOffer.templateId'))}
                data-testid={testIds.settings.serviceOrderVariantsDetail('offerSeries')}
              />
            </Box>
          </HStack>
        )}
        {shouldShowHandlingProtocol && (
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="documents.orderHandlingProtocol.templateId"
                type="choice"
                label={getTemplateLabel('entity.order.labels.handlingProtocol')}
                options={handlingProtocolData.options}
                isLoading={isLoadingAvailableDocuments}
                data-testid={testIds.settings.serviceOrderVariantsDetail(
                  'handlingProtocolTemplate'
                )}
              />
            </Box>
            <Box flex={1} />
          </HStack>
        )}
        {shouldShowResolutionProtocol && (
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="documents.orderResolutionProtocol.templateId"
                type="choice"
                label={getTemplateLabel('entity.order.labels.resolutionProtocol')}
                options={resolutionProtocolData.options}
                isLoading={isLoadingAvailableDocuments}
                data-testid={testIds.settings.serviceOrderVariantsDetail(
                  'resolutionProtocolTemplate'
                )}
              />
            </Box>
            <Box flex={1} />
          </HStack>
        )}
        <HStack spacing={4}>
          <Box flex={1}>
            <FormField
              control={props.control}
              name="documents.serviceOrderIssue.templateId"
              type="choice"
              label={getTemplateLabel('entity.order.labels.serviceOrderIssue')}
              options={serviceOrderIssueData.options}
              isLoading={isLoadingAvailableDocuments}
              data-testid={testIds.settings.serviceOrderVariantsDetail('serviceOrderIssueTemplate')}
            />
          </Box>
          <Box flex={1}>
            <FormField
              isRequired
              control={props.control}
              name="documents.serviceOrderIssue.docSeriesId"
              type="choice"
              label={getSeriesLabel('entity.order.labels.serviceOrderIssue')}
              options={getSeriesOptions('workshop/service_order_issue')}
              isDisabled={isNilOrEmpty(
                props.formApi.watch('documents.serviceOrderIssue.templateId')
              )}
              data-testid={testIds.settings.serviceOrderVariantsDetail('serviceOrderIssueSeries')}
            />
          </Box>
        </HStack>
        <HStack spacing={4}>
          <Box flex={1}>
            <FormField
              control={props.control}
              name="documents.serviceOrderReturn.templateId"
              type="choice"
              label={getTemplateLabel('entity.order.labels.serviceOrderReturn')}
              options={serviceOrderReturnData.options}
              isLoading={isLoadingAvailableDocuments}
              data-testid={testIds.settings.serviceOrderVariantsDetail(
                'serviceOrderReturnTemplate'
              )}
            />
          </Box>
          <Box flex={1}>
            <FormField
              isRequired
              control={props.control}
              name="documents.serviceOrderReturn.docSeriesId"
              type="choice"
              label={getSeriesLabel('entity.order.labels.serviceOrderReturn')}
              options={getSeriesOptions('workshop/service_order_parts_return')}
              isDisabled={isNilOrEmpty(
                props.formApi.watch('documents.serviceOrderReturn.templateId')
              )}
              data-testid={testIds.settings.serviceOrderVariantsDetail('serviceOrderReturnSeries')}
            />
          </Box>
        </HStack>
        <HStack spacing={4}>
          <Box flex={1}>
            <FormField
              control={props.control}
              name="documents.fastTrackIssue.isEnable"
              type="switch"
              label={i18n.t('entity.warehouse.labels.enableFastTrackIssue')}
              data-testid={testIds.settings.serviceOrderVariantsDetail('enableFastTrackIssue')}
            />
          </Box>
        </HStack>
        <Show when={isFastTrackEnabled}>
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="documents.fastTrackIssue.templateId"
                type="choice"
                label={i18n.t('entity.warehouse.labels.fastTrackTemplate')}
                options={fastTrackData.options}
                data-testid={testIds.settings.serviceOrderVariantsDetail('fastTrackIssueTemplate')}
              />
            </Box>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="documents.fastTrackIssue.docSeriesId"
                type="choice"
                label={i18n.t('entity.warehouse.labels.fastTrackSeries')}
                options={getOptionsFromSeries(series, 'warehouse/fast-track')}
                data-testid={testIds.settings.serviceOrderVariantsDetail('fastTrackIssueSeries')}
              />
            </Box>
          </HStack>
        </Show>
      </VStack>
      <Space vertical={18} />
      <SettingsFooter
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            title: i18n.t('general.actions.discardChanges'),
            onClick: () => navigate(serviceOrderVariantsRoutes.overview),
          },
          {
            type: 'form-button',
            control: props.control,
            buttonType: 'submit',
            variant: 'primary',
            title: i18n.t('general.actions.saveChanges'),
          },
        ]}
        data-testid={testIds.settings.serviceOrderVariantsDetail('footer')}
      />
    </SettingsSection>
  );
}

const getTemplateLabel = (key: string) =>
  `${i18n.t(key)} (${i18n.t('general.labels.template').toLocaleLowerCase()})`;

const getSeriesLabel = (key: string) =>
  `${i18n.t(key)} (${i18n.t('entity.document.labels.documentSerie').toLocaleLowerCase()})`;
